import React from 'react';
import { useUSStatesGeo } from './shared/useUSStatesGeo';
import { transformCoord, transformFeature, NAME_TO_ABBR, STATE_LABEL_RAW } from './shared/usMapProjection';
import NationalChoropleth, { type ChoroplethLabel } from './NationalChoropleth';

// CTV-445: national state choropleth for the report's geographic section — the
// State analogue of the ZIP heatmap. Thin adapter: loads the US-states GeoJSON,
// keys features by their 2-letter code, and hands the rendering to
// NationalChoropleth (shared with the DMA map).

interface StateDatum {
  state: string; // 2-letter code
  impressions: number;
  completes: number;
}

const stateKeyOf = (f: GeoJSON.Feature): string | null =>
  NAME_TO_ABBR[(f.properties as Record<string, string>)?.name || ''] || null;
const stateNameOf = (f: GeoJSON.Feature): string => (f.properties as Record<string, string>)?.name || '';

// State abbreviations at their hand-tuned (AK/HI/PR-shifted) positions.
const stateLabels: ChoroplethLabel[] = STATE_LABEL_RAW.map(([text, lng, lat]) => {
  const [tLng, tLat] = transformCoord(lng, lat);
  return { position: [tLng, tLat], text, key: text };
});

const StateChoropleth: React.FC<{
  byState: StateDatum[];
  accentColor?: string;
  highlightKey?: string | null;
  highlightColor?: string;
}> = ({ byState, accentColor, highlightKey, highlightColor }) => {
  const { data: raw } = useUSStatesGeo();

  const geojson = React.useMemo<GeoJSON.FeatureCollection | null>(
    () => (raw ? { ...raw, features: raw.features.map(transformFeature) } : null),
    [raw],
  );

  const valueByKey = React.useMemo(() => {
    const m = new Map<string, number>();
    for (const s of byState) m.set(s.state, s.impressions);
    return m;
  }, [byState]);

  return (
    <NationalChoropleth
      geojson={geojson}
      valueByKey={valueByKey}
      keyOf={stateKeyOf}
      nameOf={stateNameOf}
      labels={stateLabels}
      idPrefix="state"
      // States are large — keep more national context than the DMA map.
      maxZoom={4}
      accentColor={accentColor}
      highlightKey={highlightKey}
      highlightColor={highlightColor}
    />
  );
};

export default StateChoropleth;
